const HERO_IMAGE =
  'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1600&q=80';

/** Featured guide: goalkeeper drills with free YouTube training videos */
export const GOALKEEPER_DRILLS_POST = {
  id: 'featured-goalkeeper-drills-youtube',
  slug: 'soccer-goalkeeper-drills-free-youtube-videos',
  title: 'Soccer Goalkeeper Drills + Free YouTube Training Videos',
  excerpt:
    'Footwork, handling, diving, reactions, distribution, 1v1s, and crosses—drill ideas for rec and club keepers with links to famous free sessions on YouTube.',
  publishedAt: '2026-07-22T00:15:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'GK Training Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'More position training guides',
  relatedPosts: [
    {
      slug: 'intermediate-soccer-goalkeeper-practice-warm-up-wall-setup',
      title: 'Intermediate Goalkeeper Practice: Warm-Up, Wall Setup & Session Ideas',
      blurb: 'match-ready warm-up and free-kick wall defense.',
    },
    {
      slug: 'soccer-defender-drills-free-youtube-videos',
      title: 'Soccer Defender Drills + Free YouTube Training Videos',
      blurb: '1v1 defending, tackling, and positioning.',
    },
    {
      slug: 'soccer-forward-striker-drills-free-youtube-videos',
      title: 'Soccer Forward & Striker Drills + Free YouTube Videos',
      blurb: 'finishing, movement, and shooting.',
    },
    {
      slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
      title: 'Open Soccer Fields Near Powell, Dublin, Lewis Center & Delaware',
      blurb: 'find a park to run these sessions.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '8 areas', label: 'Footwork · Handling · Diving · More' },
        { value: '20+', label: 'Drill ideas' },
        { value: '15', label: 'Free YouTube sessions' },
        { value: 'All levels', label: 'Rec to competitive' },
      ],
    },
    {
      type: 'panel',
      title: 'How to use this guide',
      paragraphs: [
        'Each section lists drills you can run with one server (coach, parent, or teammate) plus hand-picked YouTube sessions from well-known goalkeeper coaches. All linked videos are free to watch on YouTube.',
        'Start with footwork and set position, then layer handling, diving, and game situations. Youth keepers: shorten reps and use smaller goals. Adults: add speed and harder services.',
      ],
      chips: ['Free on YouTube', 'Solo or partner', 'Rec & club', 'Ohio teams'],
    },
    {
      type: 'panel',
      title: '1 — Footwork & set position',
      list: [
        'Tape square (4 cones): shuffle → forward → shuffle → back — 3 reps each direction, then add ball at feet.',
        'Goal-line to 6-yard box: quick steps to near post, center, far post; finish in set shape every time.',
        'Mirror walk: server moves laterally; keeper mirrors without crossing feet.',
        'Set position check: knees bent, weight forward, hands ready, eyes on ball — hold 2 sec before every catch.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — footwork & fundamentals',
      videos: [
        {
          title: 'Goalkeeper Footwork Drills at home',
          channel: 'International Goalkeeper Coaches Conference (Phil Wheddon)',
          description:
            'Tape a square on the floor and work short, game-realistic steps with a ball—no partner required.',
          url: 'https://www.youtube.com/watch?v=y5DPfp1-oGc',
        },
        {
          title: 'Beginner Goalkeeper Training: Basic Fundamentals GK Session',
          channel: 'ARS Goalkeeping (Alberto Ruiz)',
          description:
            'Movement across goal, balance, and the proper keeper set position—ideal for new GKs.',
          url: 'https://www.youtube.com/watch?v=TF--UuHQGYY',
        },
        {
          title: 'Add this to your Goalkeeper Warmup',
          channel: 'Pro GK Academy (Omar Zeenni)',
          description: 'Short activation sequence you can bolt onto the start of any session.',
          url: 'https://www.youtube.com/watch?v=3p98eaLJ79w',
        },
      ],
    },
    {
      type: 'panel',
      title: '2 — Handling & shot stopping',
      list: [
        'Kneeling catches: server 8 yds out, chest-height and waist-height — soft hands, elbows in.',
        'Collapse save: from set, drop to knees for low central shot; progress to low dive.',
        'Three-cone arc: server moves along arc; keeper sets before each strike to near/far/center.',
        'Recovery save: after first hold, server plays rebound within 3 seconds — get up fast.',
        'W basket / contour catches: scoop low balls; basket high balls into chest.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — handling & shot stopping',
      videos: [
        {
          title: 'Full Handling & Shot Stopping Session | 60 in 5 Goalkeeper Training',
          channel: 'Pro GK Academy',
          description: 'Full progressive session from basic catches through game-speed shot stopping.',
          url: 'https://www.youtube.com/watch?v=6ebJt9soY_s',
        },
        {
          title: '15 Goalkeeper Drills w/ Progressions | Part 1',
          channel: 'Pro GK Academy',
          description: 'Classic drill library with progressions—great for building a weekly plan.',
          url: 'https://www.youtube.com/watch?v=7n51nnJPT6w',
        },
        {
          title: 'Goalkeeper training | Get up quickly! | Diving | Dive return | Handling',
          channel: 'Andy #1 (1.9M+ views)',
          description: 'Short drill chaining catch, knee dive, dive return, and second save.',
          url: 'https://www.youtube.com/watch?v=G0mCq8Muylk',
        },
      ],
    },
    {
      type: 'panel',
      title: '3 — Diving (low, mid & high)',
      list: [
        'Knee dive to cone: collapse to one side, hand behind ball, no slapping — 8 reps per side.',
        'Step-power-step dive: near-post and far-post from set; emphasize leading knee and hip drive.',
        'High-ball basket: server tosses underhand; keeper attacks ball at highest point with top hand.',
        'Cross-bar challenge: partner throws or kicks to upper corners; focus on last explosive step.',
        'Dive-return line: after save, roll up and back to feet before next ball (no resting on knees).',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — diving technique',
      videos: [
        {
          title: 'Goalkeeper Training: How To Make High Diving Saves',
          channel: 'ARS Goalkeeping',
          description: 'Breakdown of footwork, power step, and hand shape for top-corner saves.',
          url: 'https://www.youtube.com/watch?v=qM11UbeUr_M',
        },
        {
          title: '5 Angle & Reaction Drills | LAFC 2 Goalkeeper Training | Full Session',
          channel: 'Pro GK Academy',
          description: 'Acceleration, deceleration, and angled shot stopping used with pro keepers.',
          url: 'https://www.youtube.com/watch?v=PzdSZhsi1Oo',
        },
      ],
    },
    {
      type: 'panel',
      title: '4 — Reactions & close-range',
      list: [
        'Wall rebound: strike ball against wall 6 yds away; react to unpredictable return.',
        'Two-ball rapid fire: server holds two balls, releases either hand — keeper reacts.',
        'Gate saves: two cones 3 yds apart; shots only through gate to force quick feet.',
        'Deflection drill: server strikes through mannequin or another player’s legs.',
        'Reset drill: after save, touch cone on post before next shot.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — reactions & angles',
      videos: [
        {
          title: 'Warmup Activation',
          channel: 'Pro GK Academy',
          description: 'Pre-match activation focused on sharp feet and ready shape.',
          url: 'https://www.youtube.com/watch?v=8rzUoUu8by4',
        },
        {
          title: 'Crossing Session',
          channel: 'Pro GK Academy',
          description: 'Crosses, cutbacks, and recovery movements in one full session.',
          url: 'https://www.youtube.com/watch?v=O7jobwVyt_0',
        },
      ],
    },
    {
      type: 'panel',
      title: '5 — Distribution (throws, kicks & passes)',
      list: [
        'Bowling roll: accurate along ground to targets 10–20 yds.',
        'Overarm throw: distance and height to wing targets; non-throwing arm points at target.',
        'Goal kick: place ball, 5-step run-up, strike through center for driven pass.',
        'Side volley & half volley: turn body open, strike through ball to wide players.',
        'Pressure pass: receive back pass from defender, one-touch play to fullback under pressure.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — distribution',
      videos: [
        {
          title: 'The COMPLETE Goalkeeper Distribution Tutorial',
          channel: "Conor O'Keefe / ARS Goalkeeping",
          description:
            'Eight techniques: short pass, driven pass, clip, goal kick, roll, throw, side volley, and half volley.',
          url: 'https://www.youtube.com/watch?v=Y_Oll67U9IQ',
        },
      ],
    },
    {
      type: 'panel',
      title: '6 — 1v1 situations',
      list: [
        'Narrow the angle: come off line quickly but under control; stay big with arms.',
        'Block save: when striker takes heavy touch wide, slide block with trailing leg covered.',
        'Spread & steal: wait for heavy touch, then smother at feet.',
        'Fake shot reaction: server dribbles in; keeper sets, then reacts to final touch.',
        'Recovery run: start on goal line; striker released on through ball — timing of sprint.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — 1v1 saves',
      videos: [
        {
          title: 'How to save EVERY 1v1 as a Goalkeeper',
          channel: 'RushGK',
          description: 'Popular breakdown of positioning, patience, and spread saves.',
          url: 'https://www.youtube.com/watch?v=bs1SEca_JW8',
        },
        {
          title: 'How to stop more 1 v 1s as a GK',
          channel: 'Keeperstop',
          description: 'Coaching points for closing space and forcing the attacker wide.',
          url: 'https://www.youtube.com/watch?v=51ps5p36dw0',
        },
      ],
    },
    {
      type: 'panel',
      title: '7 — Crosses & high balls',
      list: [
        'Drop step: start on 6-yard line; server serves from wing — judge flight, attack ball at peak.',
        'Box drill: server varies service short, mid, and deep; keeper calls “Keeper!” or “Away!”',
        'Back-to-bar: start facing goal; turn on call and deal with ball over head.',
        'Punch clear: under pressure, punch through ball with fist above attackers.',
        'Catch-and-distribute: claim cross, then play quick throw to fullback target.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — crosses & aerial balls',
      videos: [
        {
          title: 'The Best Goalkeeper Training - The best crossing tips',
          channel: 'Prime Focus Goalkeeping',
          description: 'Positioning and decision-making when dealing with whipped crosses.',
          url: 'https://www.youtube.com/watch?v=nrgt5almRls',
        },
      ],
    },
    {
      type: 'panel',
      title: '8 — Sample 60-minute session (rec team)',
      orderedList: [
        '10 min — activation & footwork square (see section 1 videos).',
        '15 min — handling ladder: kneeling → standing → light dive (section 2).',
        '15 min — angle shot stopping with 3 cone arc (sections 2–3).',
        '10 min — distribution: 10 rolls, 10 throws, 5 goal kicks each side.',
        '10 min — 1v1 or cross finishing (sections 6–7).',
      ],
      callout:
        'Need a field? See our guide to open soccer parks near Powell, Dublin, Lewis Center, and Delaware.',
    },
    {
      type: 'panel',
      title: 'Equipment checklist',
      list: [
        'Match or training gloves (plus spare pair in wet weather).',
        '8–12 cones, tape for footwork box, and a few balls.',
        'Portable goals or full-size goal with anchored nets.',
        'Optional: rebounder, mannequin, or pop-up target for distribution.',
      ],
    },
  ],
  referenceLinks: [
    {
      label: 'ARS Goalkeeping — YouTube channel',
      url: 'https://www.youtube.com/@ARSGoalkeeping',
    },
    {
      label: 'Pro GK Academy — YouTube channel',
      url: 'https://www.youtube.com/@progkacademy',
    },
    {
      label: 'RushGK — YouTube channel',
      url: 'https://www.youtube.com/@RushGK',
    },
    {
      label: 'Keeperstop — YouTube channel',
      url: 'https://www.youtube.com/@keeperstop',
    },
    {
      label: 'FIFA Training Centre — learning to dive high',
      url: 'https://www.fifatrainingcentre.com/en/environment/fifa-goalkeeper-training/goalkeeping-fundamentals/learning-to-dive-high.php',
    },
  ],
};
